import { VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'
import { AlertOctagon, AlertTriangle, Trash2, X } from 'react-feather'

import { CircleCheckFull } from '@frame/images/icons/circle-check-full'
import { cn } from '@frame/lib/utils/cn'

import { Button } from './button'

const alertVariants = cva('flex w-full overflow-hidden rounded-md border-1.5 border-solid bg-white shadow', {
  variants: {
    variant: {
      warning: 'border-warning-600 text-warning-600',
      success: 'border-success-600 text-success-600',
      error: 'border-error-600 text-error-600',
    },
  },
  defaultVariants: {
    variant: 'warning',
  },
})

const alertIconVariants = cva('flex items-center p-4', {
  variants: {
    variant: {
      warning: 'bg-warning-200',
      success: 'bg-success-100',
      error: 'bg-error-200',
    },
  },
  defaultVariants: {
    variant: 'warning',
  },
})

const AlertIcon = ({ variant }: { variant: VariantProps<typeof alertVariants>['variant'] }) => {
  switch (variant) {
    case 'warning':
      return AlertTriangle
    case 'success':
      return CircleCheckFull
    case 'error':
      return AlertOctagon
    default:
      return null
  }
}

type AlertProps = {
  variant?: VariantProps<typeof alertVariants>['variant']
  action?: { show?: boolean; handler?: () => void }
  borderless?: boolean
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps & React.HTMLAttributes<HTMLDivElement>>(
  ({ children, className, variant = 'warning', action, borderless, ...props }, ref) => {
    const Icon = AlertIcon({ variant })

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          alertVariants({ variant }),
          borderless && 'items-center rounded-none border-none bg-transparent shadow-none',
          className
        )}
        {...props}
      >
        <div className={cn(alertIconVariants({ variant }), borderless && 'bg-transparent')}>
          {Icon ? <Icon className="h-4 w-4 text-current" /> : null}
        </div>
        <div
          className={cn(
            'flex flex-1 shrink-0 items-center rounded-md px-4 py-3 text-sm text-current',
            borderless && 'pl-0'
          )}
        >
          <div className="flex-1 shrink-0">{children}</div>
          {action?.show ? (
            <Button
              size="small"
              border={false}
              className="ml-2 !w-auto bg-transparent !text-inherit"
              onClick={action.handler}
            >
              {borderless ? (
                <Trash2 className="h-4 w-4 text-moss-700" />
              ) : (
                <X className="h-[1.25rem] w-[1.25rem] text-current" />
              )}
            </Button>
          ) : null}
        </div>
      </div>
    )
  }
)

Alert.displayName = 'Alert'
